import React, { useState, useEffect } from 'react';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from './firebase'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function AdminAreasSociales({
  edificioActual,
  areasSociales,
  reservas,
  mostrandoModalArea,
  setMostrandoModalArea,
  formArea,
  setFormArea,
  nuevoDiaInput,
  setNuevoDiaInput,
  nuevaHoraInput,
  setNuevaHoraInput,
  agregarDiaForm,
  eliminarDiaForm,
  agregarHoraForm,
  eliminarHoraForm,
  guardarAreaSocial,
  cancelarReserva,
  coleccionAreas,
  coleccionReservas,
  obtenerRegistros
}) {
  const [passwordReserva, setPasswordReserva] = useState('');
  const [guardandoPass, setGuardandoPass] = useState(false);

  // 📄 Estados para la Declaración Jurada de Reservas
  const [archivoDeclaracion, setArchivoDeclaracion] = useState(null);
  const [subiendoDeclaracion, setSubiendoDeclaracion] = useState(false);
  const [declaracionJuradaUrl, setDeclaracionJuradaUrl] = useState('');

  // ⏳ Estado para el plazo máximo de anticipación de reservas (en meses)
  const [plazoAnticipacionMeses, setPlazoAnticipacionMeses] = useState('');
  const [guardandoPlazo, setGuardandoPlazo] = useState(false);

  // 💰 Estados para el control del fondo / monto inicial de reservas
  const [montoInicialInput, setMontoInicialInput] = useState('');
  const [guardandoMontoInicial, setGuardandoMontoInicial] = useState(false);

  // 📌 Estados para la Nota Importante visible en el portal de propietarios y su PDF adjunto opcional
  const [notaImportante, setNotaImportante] = useState('');
  const [archivoNotaPdf, setArchivoNotaPdf] = useState(null);
  const [notaImportantePdfUrl, setNotaImportantePdfUrl] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);

  // 🔍 Estados para los filtros de año y mes en el reporte de reservas
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroMes, setFiltroMes] = useState('');

  // Nombre de usuario fijo para este edificio
  const prefijoEdificio = edificioActual?.id ? edificioActual.id.toLowerCase().replace(/\s+/g, '') : 'edificio';
  const usuarioReservaSugerido = `${prefijoEdificio}reservas`;

  useEffect(() => {
    if (edificioActual?.reservaPassword) {
      setPasswordReserva(edificioActual.reservaPassword);
    } else {
      setPasswordReserva('');
    }

    if (edificioActual?.declaracionJuradaUrl) {
      setDeclaracionJuradaUrl(edificioActual.declaracionJuradaUrl);
    } else {
      setDeclaracionJuradaUrl('');
    }

    if (edificioActual?.plazoAnticipacionMeses !== undefined) {
      setPlazoAnticipacionMeses(edificioActual.plazoAnticipacionMeses);
    } else {
      setPlazoAnticipacionMeses('');
    }

    if (edificioActual?.montoInicialReservas !== undefined) {
      setMontoInicialInput(edificioActual.montoInicialReservas);
    } else {
      setMontoInicialInput('');
    }

    if (edificioActual?.notaImportanteReservas !== undefined) {
      setNotaImportante(edificioActual.notaImportanteReservas);
    } else {
      setNotaImportante('');
    }

    if (edificioActual?.notaImportantePdfUrl) {
      setNotaImportantePdfUrl(edificioActual.notaImportantePdfUrl);
    } else {
      setNotaImportantePdfUrl('');
    }
  }, [edificioActual]);

  const actualizarPasswordReserva = async (e) => {
    e.preventDefault();
    if (!edificioActual?.id) return;
    if (!passwordReserva.trim()) {
      alert("⚠️ Ingresa una contraseña válida.");
      return;
    }

    try {
      setGuardandoPass(true);
      const edifRef = doc(db, 'edificios_mmdl', edificioActual.id);
      await updateDoc(edifRef, {
        reservaPassword: passwordReserva.trim()
      });
      alert(`✅ Contraseña del portal de reservas actualizada correctamente.\nUsuario: ${usuarioReservaSugerido}`);
      await obtenerRegistros();
    } catch (error) {
      console.error("Error al actualizar contraseña de reservas:", error);
      alert("Hubo un error al guardar la contraseña.");
    } finally {
      setGuardandoPass(false);
    }
  };

  const actualizarPlazoAnticipacion = async (e) => {
    e.preventDefault();
    if (!edificioActual?.id) return;

    try {
      setGuardandoPlazo(true);
      const edifRef = doc(db, 'edificios_mmdl', edificioActual.id);
      const valorPlazo = plazoAnticipacionMeses === '' ? '' : parseInt(plazoAnticipacionMeses, 10);
      await updateDoc(edifRef, {
        plazoAnticipacionMeses: isNaN(valorPlazo) ? '' : valorPlazo
      });
      alert("✅ Plazo máximo de anticipación actualizado correctamente.");
      await obtenerRegistros();
    } catch (error) {
      console.error("Error al actualizar plazo de anticipación:", error);
      alert("❌ Hubo un error al guardar el plazo.");
    } finally {
      setGuardandoPlazo(false);
    }
  };

  const actualizarMontoInicial = async (e) => {
    e.preventDefault();
    if (!edificioActual?.id) return;

    try {
      setGuardandoMontoInicial(true);
      const edifRef = doc(db, 'edificios_mmdl', edificioActual.id);
      await updateDoc(edifRef, {
        montoInicialReservas: parseFloat(montoInicialInput) || 0
      });
      alert("✅ Monto inicial de reservas actualizado correctamente.");
      await obtenerRegistros();
    } catch (error) {
      console.error("Error al actualizar monto inicial:", error);
      alert("❌ Hubo un error al guardar el monto inicial.");
    } finally {
      setGuardandoMontoInicial(false);
    }
  };

  // 🔄 Función inteligente para retornar o anular la devolución de garantía (Toggle automático)
  const retornarGarantiaGrupo = async (grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    const valorGarantia = obtenerValorNumerico(area?.garantia);

    if (valorGarantia <= 0) {
      alert("⚠️ Esta reserva no cuenta con un monto de garantía aplicable.");
      return;
    }

    const yaDevuelta = grupo.estadoGarantia === 'Devuelta';

    const mensaje = yaDevuelta
      ? `¿Deseas ANULAR la devolución de garantía por S/ ${valorGarantia.toFixed(2)} para el Dpto. ${grupo.departamento}? El monto volverá a sumarse al total recaudado.`
      : `¿Estás seguro de registrar la devolución de la garantía por S/ ${valorGarantia.toFixed(2)} para el Dpto. ${grupo.departamento}? Esto se descontará del total recaudado.`;

    if (window.confirm(mensaje)) {
      try {
        await Promise.all(grupo.ids.map(id => 
          updateDoc(doc(db, coleccionReservas, id), {
            estadoGarantia: yaDevuelta ? '' : 'Devuelta'
          })
        ));

        if (obtenerRegistros) {
          await obtenerRegistros();
        }

        alert(yaDevuelta ? "✅ Devolución anulada. El fondo ha sido restaurado." : "✅ Garantía devuelta y descontada del total con éxito.");
      } catch (error) {
        console.error("Error al modificar garantía:", error);
        alert("❌ Hubo un error al actualizar el estado de la garantía.");
      }
    }
  };

  // ❌ Función para alternar si la garantía no fue pagada (para excluirla/incluirla en el total)
  const toggleGarantiaNoPagada = async (grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    const valorGarantia = obtenerValorNumerico(area?.garantia);

    if (valorGarantia <= 0) {
      alert("⚠️ Esta reserva no cuenta con un monto de garantía aplicable.");
      return;
    }

    const noPagadaActual = grupo.estadoGarantia === 'No pagada';

    const mensaje = noPagadaActual
      ? `¿Deseas marcar la garantía como PAGADA para el Dpto. ${grupo.departamento}? El monto volverá a sumarse al total recaudado.`
      : `¿Deseas marcar la garantía como NO PAGADA para el Dpto. ${grupo.departamento}? El monto dejará de sumarse al total recaudado.`;

    if (window.confirm(mensaje)) {
      try {
        const nuevoEstado = noPagadaActual ? '' : 'No pagada';

        await Promise.all(grupo.ids.map(id => 
          updateDoc(doc(db, coleccionReservas, id), {
            estadoGarantia: nuevoEstado
          })
        ));

        if (obtenerRegistros) {
          await obtenerRegistros();
        }

        alert(noPagadaActual ? "✅ Garantía marcada como pagada. Se ha vuelto a sumar al total." : "✅ Garantía marcada como no pagada. Se excluyó del total recaudado.");
      } catch (error) {
        console.error("Error al actualizar estado de garantía:", error);
        alert("❌ Hubo un error al actualizar el estado de la garantía.");
      }
    }
  };

  // 📌 Guardar Nota Importante junto con la subida opcional de su PDF adjunto
  const guardarNotaImportante = async (e) => {
    e.preventDefault();
    if (!edificioActual?.id) return;

    try {
      setGuardandoNota(true);
      let urlPdfFinal = notaImportantePdfUrl;

      if (archivoNotaPdf) {
        const storageRef = ref(storage, `notas_reservas_${edificioActual.id}/nota_importante_${Date.now()}.pdf`);
        await uploadBytes(storageRef, archivoNotaPdf);
        urlPdfFinal = await getDownloadURL(storageRef);
      }

      const edifRef = doc(db, 'edificios_mmdl', edificioActual.id);
      await updateDoc(edifRef, {
        notaImportanteReservas: notaImportante.trim(),
        notaImportantePdfUrl: urlPdfFinal
      });

      setNotaImportantePdfUrl(urlPdfFinal);
      setArchivoNotaPdf(null);
      alert("✅ Nota importante y archivo adjunto actualizados correctamente para el portal de propietarios.");
      await obtenerRegistros();
    } catch (error) {
      console.error("Error al guardar nota importante:", error);
      alert("❌ Hubo un error al guardar la nota importante.");
    } finally {
      setGuardandoNota(false);
    }
  };

  const handleEliminarPdfNota = async () => {
    if (window.confirm("⚠️ ¿Estás seguro de eliminar el archivo PDF adjunto a la nota importante?")) {
      if (!edificioActual?.id) return;
      try {
        const edifRef = doc(db, 'edificios_mmdl', edificioActual.id);
        await updateDoc(edifRef, {
          notaImportantePdfUrl: ""
        });
        setNotaImportantePdfUrl("");
        alert("🗑️ Archivo PDF adjunto eliminado correctamente.");
        await obtenerRegistros();
      } catch (error) {
        console.error("Error al eliminar el PDF de la nota:", error);
        alert("❌ Hubo un error al eliminar el documento.");
      }
    }
  };

  const handleSubirDeclaracion = async (e) => {
    e.preventDefault();
    if (!archivoDeclaracion) {
      alert("⚠️ Selecciona un archivo PDF para la Declaración Jurada.");
      return;
    }
    if (!edificioActual?.id) return;

    try {
      setSubiendoDeclaracion(true);
      const storageRef = ref(storage, `declaraciones_${edificioActual.id}/declaracion_jurada_${Date.now()}.pdf`);
      await uploadBytes(storageRef, archivoDeclaracion);
      const urlDescarga = await getDownloadURL(storageRef);

      const edifRef = doc(db, 'edificios_mmdl', edificioActual.id);
      await updateDoc(edifRef, {
        declaracionJuradaUrl: urlDescarga
      });

      setDeclaracionJuradaUrl(urlDescarga);
      setArchivoDeclaracion(null);
      alert("✅ ¡Declaración Jurada subida y actualizada con éxito!");
      await obtenerRegistros();
    } catch (error) {
      console.error("Error al subir declaración jurada:", error);
      alert("❌ Hubo un error al subir el documento.");
    } finally {
      setSubiendoDeclaracion(false);
    }
  };

  const handleEliminarDeclaracion = async () => {
    if (window.confirm("⚠️ ¿Estás seguro de eliminar la Declaración Jurada actual del edificio?")) {
      if (!edificioActual?.id) return;
      try {
        const edifRef = doc(db, 'edificios_mmdl', edificioActual.id);
        await updateDoc(edifRef, {
          declaracionJuradaUrl: ""
        });
        setDeclaracionJuradaUrl("");
        alert("🗑️ Declaración Jurada eliminada correctamente.");
        await obtenerRegistros();
      } catch (error) {
        console.error("Error al eliminar la declaración jurada:", error);
        alert("❌ Hubo un error al eliminar el documento.");
      }
    }
  };

  // ✏️ Función para preparar la edición de un área existente
  const iniciarEdicionArea = (area) => {
    setFormArea({
      id: area.id,
      nombre: area.nombre || '',
      costo: area.costo !== undefined && area.costo !== null ? area.costo : '',
      garantia: area.garantia !== undefined && area.garantia !== null ? area.garantia : '',
      horariosPorDia: area.horariosPorDia || [],
      diasList: area.diasList || ['Lunes a Domingo'],
      horasList: area.horasList || ['09:00 - 22:00'],
      reglas: area.reglas || '',
      limpieza: area.limpieza || 'Limpieza incluida',
      bloqueoDiaCompleto: area.bloqueoDiaCompleto || false,
      nuevoBloqueDias: '',
      nuevoBloqueHoras: ''
    });
    setMostrandoModalArea(true);
  };

  // 📦 Agrupación lógica de reservas múltiples por lote en el panel de administración
  const reservasAgrupadasMap = {};
  reservas.forEach(r => {
    const key = `${r.areaId}_${r.fecha}_${r.departamento}_${r.voucherUrl || ''}_${r.fechaCreacion || r.id}`;
    if (!reservasAgrupadasMap[key]) {
      reservasAgrupadasMap[key] = {
        ids: [r.id],
        areaId: r.areaId,
        departamento: r.departamento,
        fecha: r.fecha,
        horarios: [r.horario],
        voucherUrl: r.voucherUrl,
        voucherGarantiaUrl: r.voucherGarantiaUrl,
        estadoGarantia: r.estadoGarantia,
        tipoLimpieza: r.tipoLimpieza,
        nombreLimpiezaExterna: r.nombreLimpiezaExterna,
        dniLimpiezaExterna: r.dniLimpiezaExterna
      };
    } else {
      reservasAgrupadasMap[key].ids.push(r.id);
      reservasAgrupadasMap[key].horarios.push(r.horario);
      if (r.voucherGarantiaUrl && !reservasAgrupadasMap[key].voucherGarantiaUrl) {
        reservasAgrupadasMap[key].voucherGarantiaUrl = r.voucherGarantiaUrl;
        reservasAgrupadasMap[key].estadoGarantia = r.estadoGarantia;
      }
    }
  });

  const ordenarHorariosCronologicamente = (horarios) => {
    return [...horarios].sort((a, b) => {
      const hA = parseInt(a.split(':')[0], 10);
      const hB = parseInt(b.split(':')[0], 10);
      const valA = hA < 6 ? hA + 24 : hA;
      const valB = hB < 6 ? hB + 24 : hB;
      return valA - valB;
    });
  };

  const listaReservasAgrupadas = Object.values(reservasAgrupadasMap).map(grupo => {
    const horariosOrdenados = ordenarHorariosCronologicamente(grupo.horarios);
    let horarioMostrado = '';
    
    if (horariosOrdenados.length === 1) {
      horarioMostrado = horariosOrdenados[0];
    } else {
      const primerHorario = horariosOrdenados[0];
      const ultimoHorario = horariosOrdenados[horariosOrdenados.length - 1];
      const partesPrimero = primerHorario.split('-');
      const partesUltimo = ultimoHorario.split('-');
      if (partesPrimero.length === 2 && partesUltimo.length === 2) {
        horarioMostrado = `${partesPrimero[0].trim()} - ${partesUltimo[1].trim()}`;
      } else {
        horarioMostrado = `${primerHorario} al ${ultimoHorario}`;
      }
    }
    return {
      ...grupo,
      horarioMostrado
    };
  });

  const listaReservasFiltradas = listaReservasAgrupadas.filter(grupo => {
    if (!grupo.fecha) return true;
    const [anioRes, mesRes] = grupo.fecha.split('-');
    const cumpleAnio = filtroAnio ? anioRes === filtroAnio : true;
    const cumpleMes = filtroMes ? mesRes === filtroMes : true;
    return cumpleAnio && cumpleMes;
  });

  const aniosDisponibles = Array.from(
    new Set(reservas.map(r => (r.fecha ? r.fecha.split('-')[0] : '')))
  ).filter(Boolean).sort().reverse();

  const obtenerValorNumerico = (valorStr) => {
    if (valorStr === undefined || valorStr === null || valorStr === '') return 0;
    if (typeof valorStr === 'number') return valorStr;
    const limpio = valorStr.toString().replace(/[^0-9.]/g, '');
    const num = parseFloat(limpio);
    return isNaN(num) ? 0 : num;
  };

  const totalRecaudadoAlquileres = listaReservasAgrupadas.reduce((acc, grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    return acc + obtenerValorNumerico(area?.costo);
  }, 0);

  // 🔄 CÁLCULO CORREGIDO: Si no está pagada o ya fue devuelta, aporta 0 al fondo retenido actual
  const totalRecaudadoGarantias = listaReservasAgrupadas.reduce((acc, grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    const valorGarantia = obtenerValorNumerico(area?.garantia);
    
    if (grupo.estadoGarantia === 'No pagada' || grupo.estadoGarantia === 'Devuelta') {
      return acc;
    }
    return acc + valorGarantia;
  }, 0);

  const totalRecaudadoBruto = totalRecaudadoAlquileres + totalRecaudadoGarantias;
  const montoBaseInicial = Number(edificioActual?.montoInicialReservas || 0);
  const granTotalRecaudado = montoBaseInicial + totalRecaudadoBruto;

  const descargarExcelReservas = () => {
    if (listaReservasAgrupadas.length === 0) {
      alert("⚠️ No hay reservas registradas para exportar.");
      return;
    }

    let csvContent = "\uFEFF";
    csvContent += "REPORTE GENERAL DE RESERVAS Y FONDO DE CAJA CHICA\n";
    csvContent += `Edificio:,${edificioActual?.id || 'General'}\n`;
    csvContent += `Monto Inicial / Fondo Anterior (S/.):,${montoBaseInicial.toFixed(2)}\n`;
    csvContent += `Recaudado en Alquileres (S/.):,${totalRecaudadoAlquileres.toFixed(2)}\n`;
    csvContent += `Recaudado en Garantías Neto (S/.):,${totalRecaudadoGarantias.toFixed(2)}\n`;
    csvContent += `Gran Total Acumulado en Caja Chica (S/.):,${granTotalRecaudado.toFixed(2)}\n\n`;
    
    csvContent += "Área Social,Departamento,Fecha,Horario,Costo (S/),Garantía (S/),Limpieza elegida,Declaración Jurada,Tipo de Pago,Voucher Garantía\n";

    listaReservasAgrupadas.forEach(grupo => {
      const area = areasSociales.find(a => a.id === grupo.areaId);
      const nombreArea = area ? `"${(area.nombre || '').replace(/"/g, '""')}"` : '"Área"';
      const dpto = `"Dpto. ${grupo.departamento}"`;
      const fecha = `"${grupo.fecha || ''}"`;
      const horario = `"${grupo.horarioMostrado || ''}"`;
      const costoNum = obtenerValorNumerico(area?.costo).toFixed(2);
      const garantiaNum = grupo.estadoGarantia === 'No pagada' ? '"0.00 (No pagada)"' : obtenerValorNumerico(area?.garantia).toFixed(2);
      const limpiezaInfo = grupo.tipoLimpieza === 'Limpieza externa contratada' 
        ? `"${grupo.tipoLimpieza} (${grupo.nombreLimpiezaExterna} - DNI: ${grupo.dniLimpiezaExterna})"` 
        : `"${grupo.tipoLimpieza || area?.limpieza || ''}"`;
      const decla = '"Aceptada"';
      const pago = grupo.voucherUrl ? '"Con Voucher"' : '"Gratis / Sin costo"';
      const voucherGarantia = grupo.voucherGarantiaUrl ? '"Presentado"' : '"No presentado"';

      csvContent += `${nombreArea},${dpto},${fecha},${horario},${costoNum},${garantiaNum},${limpiezaInfo},${decla},${pago},${voucherGarantia}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Reporte_Reservas_${edificioActual?.id || 'Edificio'}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const liberarGrupo = async (ids) => {
    if (window.confirm("¿Está seguro de liberar estos horarios? Se eliminarán de las reservas activas.")) {
      try {
        await Promise.all(ids.map(id => deleteDoc(doc(db, coleccionReservas, id))));
        if (obtenerRegistros) {
          await obtenerRegistros();
        }
        alert("✅ Horarios liberados con éxito.");
      } catch (error) {
        console.error("Error al liberar horarios:", error);
        alert("❌ Hubo un error al liberar los horarios.");
      }
    }
  };

  const areasSocialesOrdenadas = [...areasSociales].sort((a, b) => 
    (a.nombre || '').localeCompare(b.nombre || '', undefined, { numeric: true, sensitivity: 'base' })
  );

  return (
    <div>
      <h2 style={{ marginTop: 0, color: '#1e293b' }}>Áreas Sociales y Reservas</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginBottom: '25px' }}>
        
        {/* 🔐 PANEL DE CREDENCIALES */}
        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#0f172a' }}>🔒 Credenciales de Acceso</h4>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 15px 0' }}>
            Usuario fijo: <code style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '3px 6px', borderRadius: '4px', fontWeight: 'bold' }}>{usuarioReservaSugerido}</code>. Cambia su contraseña:
          </p>
          
          <form onSubmit={actualizarPasswordReserva} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input 
              type="text" 
              placeholder="Nueva contraseña..." 
              value={passwordReserva} 
              onChange={e => setPasswordReserva(e.target.value)} 
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: '1', minWidth: '140px' }} 
              required 
            />
            <button 
              type="submit" 
              disabled={guardandoPass} 
              style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '9px 12px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              {guardandoPass ? '⏳...' : '💾 Actualizar'}
            </button>
          </form>
        </div>

        {/* ⏳ PLAZO MÁXIMO DE ANTICIPACIÓN */}
        <div style={{ backgroundColor: '#fdf4ff', border: '1px solid #f5d0fe', borderRadius: '10px', padding: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#86198f' }}>⏳ Plazo de Anticipación</h4>
          <p style={{ fontSize: '0.85rem', color: '#a21caf', margin: '0 0 15px 0' }}>
            Meses máximos que los propietarios pueden reservar por adelantado (ej. 2 para Roma, 1 para Toscana, vacío para libre).
          </p>

          <form onSubmit={actualizarPlazoAnticipacion} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input 
              type="number" 
              min="1" 
              max="12" 
              placeholder="Meses (ej. 2)" 
              value={plazoAnticipacionMeses} 
              onChange={e => setPlazoAnticipacionMeses(e.target.value)} 
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #f0abfc', flex: '1', minWidth: '100px', backgroundColor: 'white' }} 
            />
            <button 
              type="submit" 
              disabled={guardandoPlazo} 
              style={{ backgroundColor: '#a21caf', color: 'white', border: 'none', padding: '9px 12px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              {guardandoPlazo ? '⏳...' : '💾 Guardar Plazo'}
            </button>
          </form>
        </div>

        {/* 📄 PANEL DECLARACIÓN JURADA */}
        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#166534' }}>📜 Declaración Jurada (PDF)</h4>
          <p style={{ fontSize: '0.85rem', color: '#15803d', margin: '0 0 15px 0' }}>
            Sube o reemplaza el documento de normas y declaración jurada.
          </p>

          <form onSubmit={handleSubirDeclaracion} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <input 
              type="file" 
              accept=".pdf" 
              onChange={e => setArchivoDeclaracion(e.target.files[0])} 
              style={{ fontSize: '0.8rem', padding: '4px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #bbf7d0' }}
            />
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <button 
                type="submit" 
                disabled={subiendoDeclaracion} 
                style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                {subiendoDeclaracion ? '⏳ Subiendo...' : 'Subir PDF 📎'}
              </button>

              {declaracionJuradaUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <a 
                    href={declaracionJuradaUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: '#0284c7', fontWeight: 'bold', fontSize: '0.8rem', textDecoration: 'underline' }}
                  >
                    Ver PDF ↗
                  </a>
                  <button 
                    type="button"
                    onClick={handleEliminarDeclaracion}
                    style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}
                    title="Eliminar Declaración Jurada"
                  >
                    🗑️ Borrar
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* 💰 CONTROL DE FONDO */}
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#1e40af' }}>💰 Control de Fondo de Reservas</h4>
          <p style={{ fontSize: '0.85rem', color: '#1e3a8a', margin: '0 0 10px 0' }}>
            Fondo acumulado (Inicial + Alquileres + Garantías Netas).
          </p>

          <form onSubmit={actualizarMontoInicial} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', padding: '5px 8px', borderRadius: '6px', border: '1px solid #93c5fd' }}>
              <span style={{ fontSize: '0.78rem', color: '#334155' }}>Monto Inicial / Anterior:</span>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input 
                  type="number" 
                  step="0.01"
                  placeholder="0.00" 
                  value={montoInicialInput} 
                  onChange={e => setMontoInicialInput(e.target.value)} 
                  style={{ width: '75px', padding: '3px 5px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }} 
                />
                <button 
                  type="submit" 
                  disabled={guardandoMontoInicial}
                  style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '4px 6px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {guardandoMontoInicial ? '...' : '💾'}
                </button>
              </div>
            </div>
          </form>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', padding: '5px 8px', borderRadius: '6px', fontSize: '0.78rem', marginTop: '8px', border: '1px solid #e2e8f0' }}>
            <span style={{ color: '#334155' }}>Recaudado (Alquileres + Garantías Netas):</span>
            <strong style={{ color: '#16a34a' }}>S/ {totalRecaudadoBruto.toFixed(2)}</strong>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#dbeafe', padding: '6px 8px', borderRadius: '6px', border: '1px solid #3b82f6', marginTop: '6px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#1e3a8a' }}>Gran Total Acumulado:</span>
            <strong style={{ fontSize: '0.95rem', color: '#1e40af' }}>S/ {granTotalRecaudado.toFixed(2)}</strong>
          </div>
        </div>

        {/* 📌 NOTA IMPORTANTE Y PDF OPCIONAL */}
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#92400e' }}>📌 Nota Importante y PDF (Propietarios)</h4>
          <p style={{ fontSize: '0.85rem', color: '#b45309', margin: '0 0 10px 0' }}>
            Anota puntos puntuales y adjunta un PDF informativo opcional si es necesario.
          </p>

          <form onSubmit={guardarNotaImportante} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <textarea 
              rows="3"
              placeholder="- Ej. Respetar los horarios de uso&#10;- Dejar limpio el salón de eventos" 
              value={notaImportante} 
              onChange={e => setNotaImportante(e.target.value)} 
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #fcd34d', fontSize: '0.82rem', resize: 'vertical', backgroundColor: 'white' }} 
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '0.78rem', color: '#92400e', fontWeight: 'bold' }}>Adjuntar PDF informativo opcional:</label>
              <input 
                type="file" 
                accept=".pdf" 
                onChange={e => setArchivoNotaPdf(e.target.files[0])} 
                style={{ fontSize: '0.78rem', padding: '3px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #fcd34d' }}
              />
            </div>

            {notaImportantePdfUrl && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'white', padding: '4px 8px', borderRadius: '4px', border: '1px solid #fcd34d' }}>
                <a 
                  href={notaImportantePdfUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ color: '#0284c7', fontWeight: 'bold', fontSize: '0.78rem', textDecoration: 'underline' }}
                >
                  Ver PDF Adjunto Actual ↗
                </a>
                <button 
                  type="button"
                  onClick={handleEliminarPdfNota}
                  style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.7rem' }}
                >
                  🗑️ Borrar PDF
                </button>
              </div>
            )}

            <button 
              type="submit" 
              disabled={guardandoNota}
              style={{ backgroundColor: '#d97706', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer', alignSelf: 'flex-end', marginTop: '4px' }}
            >
              {guardandoNota ? '⏳ Guardando...' : '💾 Guardar Nota y PDF'}
            </button>
          </form>
        </div>

      </div>

      <button 
        onClick={() => {
          setFormArea({ 
            nombre: '', 
            costo: '', 
            garantia: '', 
            horariosPorDia: [], 
            diasList: [], 
            horasList: [], 
            reglas: '', 
            limpieza: 'Limpieza incluida',
            bloqueoDiaCompleto: false,
            nuevoBloqueDias: '',
            nuevoBloqueHoras: ''
          });
          setMostrandoModalArea(true);
        }} 
        style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '20px' }}
      >
        ➕ Nueva Área
      </button>
      
      {/* 📋 MODAL DE CREACIÓN / EDICIÓN DE ÁREA SOCIAL */}
      {mostrandoModalArea && (
        <div style={{ backgroundColor: '#f0fdf4', border: '2px solid #bbf7d0', padding: '20px', borderRadius: '10px', marginBottom: '25px' }}>
          <h3 style={{ marginTop: 0, color: '#166534' }}>{formArea.id ? '✏️ Editar Área Social' : 'Registrar Nueva Área Social'}</h3>
          <form onSubmit={guardarAreaSocial} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <input type="text" placeholder="Nombre del área (Ej. 02 - Parrilla)" value={formArea.nombre} onChange={e => setFormArea({...formArea, nombre: e.target.value})} style={{ padding: '9px', borderRadius: '6px', border: '1px solid #cbd5e1' }} required />
            
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input type="number" step="0.01" placeholder="Costo alquiler (En blanco si es gratis)" value={formArea.costo} onChange={e => setFormArea({...formArea, costo: e.target.value})} style={{ flex: 1, padding: '9px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
              <input type="number" step="0.01" placeholder="Garantía (En blanco si no aplica)" value={formArea.garantia} onChange={e => setFormArea({...formArea, garantia: e.target.value})} style={{ flex: 1, padding: '9px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
            </div>
            
            {/* 🔒 BLOQUEO DE DÍA COMPLETO */}
            <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input 
                type="checkbox" 
                id="bloqueoDiaCompleto"
                checked={formArea.bloqueoDiaCompleto || false} 
                onChange={e => setFormArea({...formArea, bloqueoDiaCompleto: e.target.checked})} 
                style={{ width: '18px', height: '18px', cursor: 'pointer' }} 
              />
              <label htmlFor="bloqueoDiaCompleto" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#166534', cursor: 'pointer' }}>
                🔒 Bloquear todo el día (La reserva inhabilita la fecha completa)
              </label>
            </div>

            {/* 🧹 CONDICIÓN DE LIMPIEZA CON OPCIÓN "Libre" */}
            <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '6px', color: '#166534' }}>🧹 Condición de Limpieza:</label>
              <select 
                value={formArea.limpieza || 'Limpieza incluida'} 
                onChange={e => setFormArea({...formArea, limpieza: e.target.value})} 
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              >
                <option value="Limpieza incluida">Limpieza incluida</option>
                <option value="Limpieza no incluida">Limpieza no incluida</option>
                <option value="Limpieza a cargo del propietario">Limpieza a cargo del propietario</option>
                <option value="Libre">Libre (A elección del propietario)</option>
              </select>
            </div>

            {/* 🕒 HORARIOS DIFERENCIADOS POR DÍAS */}
            <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '6px', color: '#166534' }}>🕒 Horarios Diferenciados por Días:</label>
              <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0 0 10px 0' }}>Agrega grupos de días y sus horas específicas (ej. "Lunes a Viernes" con "10:00 - 17:00").</p>
              
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <input 
                  type="text" 
                  placeholder="Ej. Lunes a Viernes o Sábados" 
                  value={formArea.nuevoBloqueDias || ''} 
                  onChange={e => setFormArea({...formArea, nuevoBloqueDias: e.target.value})} 
                  style={{ flex: 1, minWidth: '130px', padding: '7px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }} 
                />
                <input 
                  type="text" 
                  placeholder="Ej. 10:00 - 17:00" 
                  value={formArea.nuevoBloqueHoras || ''} 
                  onChange={e => setFormArea({...formArea, nuevoBloqueHoras: e.target.value})} 
                  style={{ flex: 1, minWidth: '130px', padding: '7px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }} 
                />
                <button 
                  type="button" 
                  onClick={() => {
                    if (!formArea.nuevoBloqueDias || !formArea.nuevoBloqueHoras) {
                      alert("⚠️ Ingresa tanto los días como el horario.");
                      return;
                    }

                    let horaFinalFormateada = formArea.nuevoBloqueHoras.trim();

                    const partesRango = horaFinalFormateada.split('-').map(p => p.trim());
                    if (partesRango.length === 2) {
                      const [hInicio] = partesRango[0].split(':').map(Number);
                      const [hFin] = partesRango[1].split(':').map(Number);

                      if (!isNaN(hInicio) && !isNaN(hFin)) {
                        let adjustedHFin = hFin;
                        if (adjustedHFin <= hInicio) {
                          adjustedHFin += 24; 
                        }

                        let subHorarios = [];
                        for (let h = hInicio; h < adjustedHFin; h++) {
                          const currentH = h % 24;
                          const siguienteH = (h + 1) % 24;
                          const strH = currentH < 10 ? `0${currentH}` : `${currentH}`;
                          const strSigH = siguienteH < 10 ? `0${siguienteH}` : `${siguienteH}`;
                          subHorarios.push(`${strH}:00 - ${strSigH}:00`);
                        }
                        if (subHorarios.length > 0) {
                          horaFinalFormateada = subHorarios.join(', ');
                        }
                      }
                    }

                    const bloqueNuevo = { diasText: formArea.nuevoBloqueDias.trim(), hora: horaFinalFormateada };
                    const actualizados = [...(formArea.horariosPorDia || []), bloqueNuevo];
                    setFormArea({
                      ...formArea,
                      horariosPorDia: actualizados,
                      nuevoBloqueDias: '',
                      nuevoBloqueHoras: ''
                    });
                  }} 
                  style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.82rem' }}
                >
                  Agregar Bloque ➕
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(formArea.horariosPorDia || []).map((bloque, index) => (
                  <div key={index} style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a', padding: '6px 10px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem' }}>
                    <span style={{ color: '#92400e', fontWeight: 'bold' }}>
                      📅 {bloque.diasText} ➔ ⏰ {bloque.hora}
                    </span>
                    <button 
                      type="button" 
                      onClick={() => {
                        const filtrados = formArea.horariosPorDia.filter((_, i) => i !== index);
                        setFormArea({...formArea, horariosPorDia: filtrados});
                      }} 
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <textarea placeholder="Reglas de uso..." value={formArea.reglas} onChange={e => setFormArea({...formArea, reglas: e.target.value})} style={{ padding: '9px', borderRadius: '6px', border: '1px solid #cbd5e1' }}></textarea>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>{formArea.id ? 'Actualizar Área' : 'Guardar Área'}</button>
              <button type="button" onClick={() => setMostrandoModalArea(false)} style={{ backgroundColor: '#9ca3af', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* 📋 TARJETAS DE ÁREAS SOCIALES */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px', marginBottom: '25px' }}>
        {areasSocialesOrdenadas.map(area => {
          const costoLimpio = area.costo !== undefined && area.costo !== null && area.costo !== '' ? String(area.costo).replace(/^S\/\s*/i, '').trim() : '';
          const valorNumGarantia = obtenerValorNumerico(area.garantia);

          return (
            <div key={area.id} style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', position: 'relative' }}>
              <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '5px' }}>
                <button onClick={() => iniciarEdicionArea(area)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }} title="Editar Área">✏️</button>
                <button onClick={async () => { if(window.confirm("¿Estás seguro de eliminar esta área social?")) { await deleteDoc(doc(db, coleccionAreas, area.id)); await obtenerRegistros(); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }} title="Eliminar Área">🗑️</button>
              </div>

              <h4 style={{ margin: '0 0 5px 0' }}>
                🏡 {area.nombre}
                {area.bloqueoDiaCompleto && (
                   <span title="Bloqueo de día completo activado" style={{ marginLeft: '6px', fontSize: '0.85rem', cursor: 'help' }}>
                     🔒
                   </span>
                )}
              </h4>
              <p style={{ margin: '3px 0', fontSize: '0.85rem', color: '#0284c7', fontWeight: 'bold' }}>
                Costo: {costoLimpio && costoLimpio !== '0' && costoLimpio !== '0.00' ? `S/ ${costoLimpio}` : 'Gratis'}
              </p>
              <p style={{ margin: '3px 0', fontSize: '0.85rem', color: '#d97706', fontWeight: 'bold' }}>
                Garantía: {valorNumGarantia > 0 ? `S/ ${valorNumGarantia.toFixed(2)}` : 'Sin garantía'}
              </p>
              <p style={{ margin: '3px 0', fontSize: '0.8rem', color: '#475569' }}>🧹 Limpieza: <strong>{area.limpieza === 'Libre' ? 'Libre (A elección del propietario)' : (area.limpieza || 'Limpieza incluida')}</strong></p>
              
              <p style={{ margin: '5px 0 2px 0', fontSize: '0.8rem', color: '#334155' }}>🕒 <strong>Horarios por Días:</strong></p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '5px' }}>
                {area.horariosPorDia && area.horariosPorDia.length > 0 ? (
                  area.horariosPorDia.map((bloque, i) => (
                    <div key={i} style={{ backgroundColor: '#fef3c7', color: '#b45309', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                      📅 {bloque.diasText}: ⏰ {bloque.hora}
                    </div>
                  ))
                ) : (
                  <span style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }}>Horario estándar general</span>
                )}
              </div>

              {area.reglas ? (
                <div style={{ margin: '5px 0 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                  <strong>Reglas:</strong>
                  <ol style={{ margin: '3px 0 0 15px', paddingLeft: '0' }}>
                    {area.reglas.split('\n').map((regla, rIdx) => {
                      const reglaLimpia = regla.replace(/^\d+[.)]\s*/, '').trim();
                      return reglaLimpia ? <li key={rIdx}>{reglaLimpia}</li> : null;
                    })}
                  </ol>
                </div>
              ) : (
                <p style={{ margin: '5px 0 0 0', fontSize: '0.75rem', color: '#64748b' }}>Reglas: Ninguna</p>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h4 style={{ color: '#1e293b', margin: 0 }}>Horarios Bloqueados y Reservas:</h4>
          <button 
            onClick={descargarExcelReservas}
            style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
            title="Descargar reporte general de reservas en Excel"
          >
            📥 Descargar Excel
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select 
            value={filtroAnio} 
            onChange={e => setFiltroAnio(e.target.value)} 
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
          >
            <option value="">📅 Todos los Años</option>
            {aniosDisponibles.map(anio => (
              <option key={anio} value={anio}>{anio}</option>
            ))}
          </select>

          <select 
            value={filtroMes} 
            onChange={e => setFiltroMes(e.target.value)} 
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
          >
            <option value="">📆 Todos los Meses</option>
            <option value="01">Enero</option>
            <option value="02">Febrero</option>
            <option value="03">Marzo</option>
            <option value="04">Abril</option>
            <option value="05">Mayo</option>
            <option value="06">Junio</option>
            <option value="07">Julio</option>
            <option value="08">Agosto</option>
            <option value="09">Septiembre</option>
            <option value="10">Octubre</option>
            <option value="11">Noviembre</option>
            <option value="12">Diciembre</option>
          </select>
        </div>
      </div>

      <table className="tabla-propietarios">
        <thead>
          <tr>
            <th>Área</th>
            <th>Dpto</th>
            <th>Fecha</th>
            <th>Horario</th>
            <th>Pago / Alquiler</th>
            <th>Garantía</th>
            <th>Limpieza Elegida</th>
            <th>Declaración Jurada</th>
            <th>Voucher / Pago</th>
            <th>Voucher Garantía</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          {listaReservasFiltradas.length === 0 ? (
            <tr><td colSpan="11" style={{ textAlign: 'center', padding: '25px', color: '#666' }}>No hay reservas registradas para el período seleccionado.</td></tr>
          ) : (
            listaReservasFiltradas.map((grupo, idx) => {
              const area = areasSociales.find(a => a.id === grupo.areaId);
              const valorNumCostoReserva = obtenerValorNumerico(area?.costo);
              const valorNumGarantiaReserva = obtenerValorNumerico(area?.garantia);
              const costoLimpioReserva = area?.costo !== undefined && area?.costo !== null && area?.costo !== '' ? String(area.costo).replace(/^S\/\s*/i, '').trim() : '';

              return (
                <tr key={idx}>
                  <td>
                    {area ? area.nombre : 'Área'}
                    {area?.bloqueoDiaCompleto && (
                      <span 
                        title="Esta área tiene activado el bloqueo de día completo" 
                        style={{ marginLeft: '6px', cursor: 'help', fontSize: '0.85rem' }}
                      >
                        🔒
                      </span>
                    )}
                  </td>
                  <td>Dpto. {grupo.departamento}</td>
                  <td>{grupo.fecha}</td>
                  <td>
                    {/* 🕒 Muestra el horario seleccionado en el panel de administración incluso si el bloqueo está activo */}
                    <span style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                      🔒 {grupo.horarioMostrado}
                    </span>
                  </td>
                  <td>
                    {valorNumCostoReserva > 0 ? (
                      <span style={{ color: '#0284c7', fontWeight: 'bold', fontSize: '0.85rem' }}>S/ {costoLimpioReserva}</span>
                    ) : (
                      <span style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: 'bold' }}>Gratis</span>
                    )}
                  </td>
                  <td>
                    {valorNumGarantiaReserva > 0 ? (
                      <span style={{ color: '#d97706', fontWeight: 'bold', fontSize: '0.85rem' }}>
                        {grupo.estadoGarantia === 'No pagada' ? (
                          <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                            S/ {valorNumGarantiaReserva.toFixed(2)}
                          </span>
                        ) : (
                          `S/ ${valorNumGarantiaReserva.toFixed(2)}`
                        )}
                      </span>
                    ) : (
                      <span style={{ color: '#64748b', fontSize: '0.75rem' }}>No aplica</span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: '0.78rem', color: '#334155' }}>
                      {grupo.tipoLimpieza === 'Limpieza externa contratada' 
                        ? <><strong>Externa:</strong> {grupo.nombreLimpiezaExterna} (DNI: {grupo.dniLimpiezaExterna})</>
                        : (grupo.tipoLimpieza || area?.limpieza || 'Incluida')}
                    </span>
                  </td>
                  <td>
                    <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.75rem' }}>
                      ✅ Aceptada
                    </span>
                  </td>
                  <td>
                    {grupo.voucherUrl ? (
                      <a href={grupo.voucherUrl} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: '#0284c7', color: 'white', padding: '5px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'none', display: 'inline-block' }}>
                        👁️ Ver Voucher ↗
                      </a>
                    ) : (
                      <span style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: 'bold' }}>Gratis / Sin costo</span>
                    )}
                  </td>
                  <td>
                    {valorNumGarantiaReserva > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                        {grupo.voucherGarantiaUrl ? (
                          <a href={grupo.voucherGarantiaUrl} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: '#d97706', color: 'white', padding: '5px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'none', display: 'inline-block' }}>
                            👁️ Ver Garantía ↗
                          </a>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic' }}>Sin voucher</span>
                        )}

                        {grupo.estadoGarantia === 'Devuelta' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '3px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                              ✅ Garantía Devuelta
                            </span>
                            <button 
                              type="button"
                              onClick={() => retornarGarantiaGrupo(grupo)}
                              style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                              title="Anular devolución y volver a sumar al total"
                            >
                              ↩️ Anular / Volver a Sumar
                            </button>
                          </div>
                        ) : grupo.estadoGarantia === 'No pagada' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                              ❌ Garantía No Pagada
                            </span>
                            <button 
                              type="button"
                              onClick={() => toggleGarantiaNoPagada(grupo)}
                              style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                              title="Marcar como pagada y volver a sumar al total"
                            >
                              ✅ Marcar como Pagada
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <button 
                              type="button"
                              onClick={() => retornarGarantiaGrupo(grupo)}
                              style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer' }}
                              title="Devolver garantía y descontar del total acumulado"
                            >
                              🔄 Devolver Garantía
                            </button>
                            <button 
                              type="button"
                              onClick={() => toggleGarantiaNoPagada(grupo)}
                              style={{ backgroundColor: '#b91c1c', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                              title="Marcar que no pagaron la garantía para excluirla del total"
                            >
                              ❌ No Pagaron Garantía
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#64748b', fontSize: '0.75rem' }}>No aplica</span>
                    )}
                  </td>
                  <td>
                    <button onClick={() => liberarGrupo(grupo.ids)} style={{ backgroundColor: '#d9534f', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                      Liberar
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}